import { useMemo, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Check, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import "@/App.css";

import { MOMENTS, SERVICES, ADDONS } from "@/config/pricing";
import {
  computeTotal,
  buildWhatsAppLink,
  eventTypeFromMoments,
} from "@/lib/dirga";
import { cn } from "@/lib/utils";
import { Backdrop } from "@/components/theme/Backdrop";
import { Reveal, GoldRule } from "@/components/theme/Reveal";

import { Hero } from "@/components/dirga/Hero";
import { SelectableCard } from "@/components/dirga/SelectableCard";
import { SummaryPanel, MobileSummaryBar } from "@/components/dirga/Summary";
import { ClientForm } from "@/components/dirga/ClientForm";
import { Confirmation } from "@/components/dirga/Confirmation";

const SectionHeader = ({ step, title, subtitle }) => (
  <Reveal className="mb-10">
    <p className="font-sans text-[11px] uppercase tracking-[0.35em] text-[#8C6D32]">
      {step}
    </p>
    <h2 className="mt-3 font-serif text-4xl font-light tracking-tight text-[#111111] sm:text-5xl">
      {title}
    </h2>
    <GoldRule className="mt-5" />
    {subtitle && (
      <p className="mt-5 max-w-lg font-sans text-sm leading-relaxed text-[#8A8A8A]">
        {subtitle}
      </p>
    )}
  </Reveal>
);

function Calculator() {
  const [view, setView] = useState("build"); // "build" | "success"
  const [selectedMoments, setSelectedMoments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [styles, setStyles] = useState({}); // { idLayanan: idGayaVideo }
  const [submitting, setSubmitting] = useState(false);
  const [waLink, setWaLink] = useState("");
  const [finalTotal, setFinalTotal] = useState(0);

  const momentsRef = useRef(null);
  const formRef = useRef(null);

  const total = useMemo(() => computeTotal(selectedIds), [selectedIds]);
  const eventType = useMemo(
    () => eventTypeFromMoments(selectedMoments),
    [selectedMoments]
  );

  const scrollTo = (ref) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const toggleMoment = (id) =>
    setSelectedMoments((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );

  const toggleService = useCallback(
    (id) =>
      setSelectedIds((prev) =>
        prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
      ),
    []
  );

  // Memilih gaya video otomatis ikut memilih layanannya.
  const chooseStyle = useCallback((serviceId, styleId) => {
    setStyles((prev) => ({ ...prev, [serviceId]: styleId }));
    setSelectedIds((prev) => (prev.includes(serviceId) ? prev : [...prev, serviceId]));
  }, []);

  const removeItem = useCallback(
    (id) => setSelectedIds((prev) => prev.filter((s) => s !== id)),
    []
  );

  // Filter services by selected moments (moments are navigation only, no price).
  const visibleServices = useMemo(() => {
    if (selectedMoments.length === 0) return SERVICES;
    return SERVICES.filter((s) =>
      s.moments.some((m) => selectedMoments.includes(m))
    );
  }, [selectedMoments]);

  const photography = visibleServices.filter((s) => s.category === "Photography");
  const videography = visibleServices.filter((s) => s.category === "Videography");

  const handleContinue = () => {
    if (selectedIds.length === 0) {
      toast.error("Pilih minimal satu layanan untuk lanjut.");
      return;
    }
    scrollTo(formRef);
  };

  const handleSubmit = (form) => {
    setSubmitting(true);
    try {
      const link = buildWhatsAppLink({ selectedIds, form, total, styles });
      setWaLink(link);
      setFinalTotal(total);
      // Open WhatsApp with the pre-filled inquiry message.
      window.open(link, "_blank", "noopener,noreferrer");
      setView("success");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(
        "Terjadi kesalahan. Silakan coba lagi atau hubungi DIRGA langsung."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    setView("build");
    setSelectedIds([]);
    setSelectedMoments([]);
    setStyles({});
    window.scrollTo({ top: 0 });
  };

  if (view === "success") {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Toaster position="top-center" />
        <Confirmation total={finalTotal} whatsappLink={waLink} onBack={handleBack} />
      </div>
    );
  }

  const renderCard = (s, testPrefix = "service") => (
    <SelectableCard
      key={s.id}
      item={s}
      selected={selectedIds.includes(s.id)}
      onToggle={toggleService}
      styleId={styles[s.id]}
      onStyle={chooseStyle}
      testId={`${testPrefix}-${s.id}`}
    />
  );

  return (
    <div className="relative isolate min-h-screen bg-[#F9F8F6] text-[#111111]">
      <Toaster position="top-center" />
      <Backdrop />

      <Link
        to="/"
        className="absolute left-6 top-6 z-10 inline-flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-[#8A8A8A] hover:text-[#1C1C1C] md:left-12"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Beranda
      </Link>

      <Hero onStart={() => scrollTo(momentsRef)} />

      <div className="mx-auto max-w-7xl px-6 pb-40 md:px-12 lg:px-20 lg:pb-32">
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-16">
          {/* LEFT: steps */}
          <div>
            {/* STEP 01 — Moments */}
            <section ref={momentsRef} className="scroll-mt-24 pt-16 lg:pt-24">
              <SectionHeader
                step="Langkah 01"
                title="What are you celebrating?"
                subtitle="Mulai dengan memilih momen yang ingin kami abadikan. Bagian ini hanya menyaring layanan yang tampil — belum menambah biaya apa pun."
              />
              <Reveal>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                {MOMENTS.map((m) => {
                  const active = selectedMoments.includes(m.id);
                  return (
                    <motion.button
                      key={m.id}
                      type="button"
                      data-testid={`moment-${m.id}`}
                      onClick={() => toggleMoment(m.id)}
                      whileTap={{ scale: 0.99 }}
                      aria-pressed={active}
                      className={cn(
                        "relative border p-6 text-left transition-colors duration-300 outline-none focus-visible:ring-1 focus-visible:ring-[#1C1C1C]",
                        active
                          ? "border-[#C6A56B] bg-[#FBF7EE]"
                          : "border-[#E8E3D9] bg-white/90 hover:border-[#C6A56B]/70"
                      )}
                    >
                      <span
                        className={cn(
                          "absolute right-4 top-4 flex h-5 w-5 items-center justify-center border transition-all duration-300",
                          active
                            ? "border-[#C6A56B] bg-[#C6A56B] text-[#111111]"
                            : "border-[#D8D2C6] text-transparent"
                        )}
                      >
                        <Check className="h-3 w-3" strokeWidth={2.5} />
                      </span>
                      <h3 className="pr-6 font-serif text-2xl text-[#1C1C1C]">{m.label}</h3>
                    </motion.button>
                  );
                })}
              </div>
              </Reveal>
            </section>

            {/* STEP 02 — Services */}
            <section className="pt-20 lg:pt-28">
              <SectionHeader
                step="Langkah 02"
                title="Choose your services"
                subtitle="Pilih layanan foto dan video yang kamu butuhkan. Ketuk kartu untuk menambah atau menghapus — total langsung diperbarui."
              />

              {photography.length > 0 && (
                <div className="mb-12">
                  <p className="mb-5 font-sans text-[11px] uppercase tracking-[0.3em] text-[#1C1C1C]">
                    Photography
                  </p>
                  <Reveal>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {photography.map((s) => renderCard(s))}
                    </div>
                  </Reveal>
                </div>
              )}

              {videography.length > 0 && (
                <div>
                  <p className="mb-5 font-sans text-[11px] uppercase tracking-[0.3em] text-[#1C1C1C]">
                    Videography
                  </p>
                  <Reveal>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                      {videography.map((s) => renderCard(s))}
                    </div>
                  </Reveal>
                </div>
              )}
            </section>

            {/* Add-ons */}
            <section className="pt-20 lg:pt-28">
              <SectionHeader
                step="Tambahan"
                title="Make it yours"
                subtitle="Tambahkan sesuatu yang spesial untuk melengkapi paketmu. Semua item ini opsional."
              />
              <Reveal>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  {ADDONS.map((a) => renderCard(a, "addon"))}
                </div>
              </Reveal>
            </section>

            {/* FORM */}
            <section ref={formRef} className="scroll-mt-24 pt-24 lg:pt-32">
              <ClientForm
                selectedIds={selectedIds}
                total={total}
                defaultEventType={eventType}
                onSubmit={handleSubmit}
                submitting={submitting}
              />
            </section>
          </div>

          {/* RIGHT: sticky summary (desktop) */}
          <div className="hidden lg:block">
            <div className="pt-24">
              <SummaryPanel
                selectedIds={selectedIds}
                styles={styles}
                total={total}
                onContinue={handleContinue}
                onRemove={removeItem}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      <MobileSummaryBar
        selectedIds={selectedIds}
        styles={styles}
        total={total}
        onContinue={handleContinue}
        onRemove={removeItem}
      />
    </div>
  );
}

export default Calculator;
